// ANGULAR
import { Injectable } from '@angular/core';

// NGRX
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

// RXJS
import { Observable, of } from 'rxjs';
import { catchError, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

// EXTERNAL DEPENDENCY
import { get } from 'lodash';

// SERVICES
import { MasterBusinessService } from '../services/master-business.service';

// ACTIONS AND STATES
import * as globalActions from '../../../global/store/actions';
import { State } from '../../../root-state';
import * as businessActions from './actions';

// COMMON INTERFACES AND ENUMS
import { ModalType } from '../../../../models/common/modal/modal-config';

// CONSTANT
import { MASTER_BUSINESS_NGRX_CONSTANTS } from '../../../../constants/ngrx-store/master-business';

/**
 * This effect is for listen master business actions, execute master business service call and dispatch in master business reducer.
 * For master business api call purpose.
 *
 * @author Vikas Thakkar <vikas.thakkar@saeculumsolutions.com>
 *
 * Notes:-
 * Date: 27/04/2020 (Vikas Thakkar <vikas.thakkar@saeculumsolutions.com>)  created master business effect
 */
@Injectable()
export class MasterBusinessEffects {
  constructor(
    private actions$: Actions,
    private store: Store<State>,
    private masterBusinessService: MasterBusinessService) { }

  /**
   *  Master Business Effect
   * @param type : MASTER_BUSINESS_COMMON action name.
   * @param payload : object to identify common master business method base on module and method.
   * @returns Empty action dispatch to reducer.
   */
  masterBusinessCommonEffect$ = createEffect(() => this.actions$.pipe(
    ofType<businessActions.MasterBusinessCommonAction>(businessActions.ActionTypes.MASTER_BUSINESS_COMMON),
    withLatestFrom(this.store),
    switchMap(([action, state]): Observable<any> => {
      // get common constant base action module and method.
      // changed to payload type from payload.data.method
      const common = MASTER_BUSINESS_NGRX_CONSTANTS[action.payload.module][action.payload.method];

      // avoid service call if data already exists in store.
      if (common.CACHING && !common.INDIVIDUAL && state.masterbusiness && state.masterbusiness[common.STATE]) {
        if (action.payload.callback) {
          action.payload.callback({ success: true, data: state.masterbusiness[common.STATE] });
        }
        // make empty action call for skip
        return of(new globalActions.EmptyAction({}));
      } else if (common.CACHING && common.INDIVIDUAL && state.masterbusiness &&
        state.masterbusiness[common.STATE] &&
        state.masterbusiness[common.STATE][action.payload.data[common.UNIQUE_KEY]]) {

        // data send from redux store if already available
        if (action.payload.callback) {
          action.payload.callback({ success: true, data: state.masterbusiness[common.STATE][action.payload.data[common.UNIQUE_KEY]] });
        }

        return of(new globalActions.EmptyAction({}));
      }

      // make api call
      return this.masterBusinessService[common.API_METHOD](action.payload.data)
        .pipe(

          mergeMap((data: any) => {
            let actions: any[] = [];

            // this code added to else condition of success notification
            // if (action.payload.callback) {
            //   action.payload.callback({ success: true, data: get(data, common.API_RESPONSE) });
            // }
            // dispatch master business success action
            actions = [new businessActions.MasterBusinessCommonSuccessAction({ data, common })];

            if (common.SUCCESS_NOTIFICATION) {
              // dispatch popup notification for open success popup

                actions.push(new globalActions.PopupNotificationAction({
                  data: data,
                  actionType: businessActions.ActionTypes.MASTER_BUSINESS_COMMON, type: ModalType.SUCCESS,
                  callback: action.payload.callback, title: common.TITLE, message: common.SUCCESS,
                  code: `${common.SUCCESS}`,
                }));

            } else {
              action.payload.callback && action.payload.callback({ success: true, data: get(data, common.API_RESPONSE) });
            }

            return actions;
          }),
          catchError(error => {
            console.error(error);
            error.error = error.error || {};
            // dispatch popup notification for open error popup
            return of(new globalActions.PopupNotificationAction({
              actionType: businessActions.ActionTypes.MASTER_BUSINESS_COMMON, type: ModalType.ERROR,
              message: error && error.error && error.error.message, callback: action.payload.callback, error: (error || error.error),
              // code: `ERROR.${error.code || error.error.code}`,
              title: common.TITLE,
            }));
          }));
    }),
  ));


}
